import React, { useState, useEffect, useRef, useMemo } from "react";
import InvoicePDFViewer from "./InvoicePDFViewer";
import UploadInvoicesBox from "./UploadInvoicesBox";
import InvoiceHeader from "./InvoiceHeader";
import UploadingInvoices from "./UploadingInvoices";
import handleUpload from "./handleUpload";
import processInvoice from "./processInvoice";
import { handleFileUpload } from "./handleFileUpload";
import createRequest from "./createRequests";
import checkIfExists from "./helpers/checkIfExists";
import fetchAndReturnMasterfile from "./helpers/fetchAndReturnMasterfile";

// Custom SVG icons as components
const ChevronDownIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    stroke="currentColor"
    strokeWidth="2"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

const UploadIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    stroke="currentColor"
    strokeWidth="2"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
);

const SendIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    stroke="currentColor"
    strokeWidth="2"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 2L11 13"></path>
    <path d="M22 2L15 22 11 13 2 9 22 2z"></path>
  </svg>
);

const FileTextIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    stroke="currentColor"
    strokeWidth="2"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    stroke="currentColor"
    strokeWidth="2"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const LoaderIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="48"
    height="48"
    stroke="currentColor"
    strokeWidth="2"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="spinner"
  >
    <line x1="12" y1="2" x2="12" y2="6"></line>
    <line x1="12" y1="18" x2="12" y2="22"></line>
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
    <line x1="2" y1="12" x2="6" y2="12"></line>
    <line x1="18" y1="12" x2="22" y2="12"></line>
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
  </svg>
);

// Static data for the component
// Remove the static projects array and use the one from props
const staticInvoicesFixed = [
  {
    id: "1",
    name: "Pro-Forma Work order.pdf",
    date: "24 Mar 2025, 10:23 AM",
    uploader: "John Doe",
    url: "https://firebasestorage.googleapis.com/v0/b/start-module-staging-2.appspot.com/o/pdfs%2FPro-Forma%20Work%20order.pdf?alt=media&token=unique1",
    requestItemData: {
      projectName: "Enterprise Project",
    },
  },
  {
    id: "2",
    name: "Invoice Q1-2025.pdf",
    date: "22 Mar 2025, 3:45 PM",
    uploader: "Jane Smith",
    url: "https://firebasestorage.googleapis.com/v0/b/start-module-staging-2.appspot.com/o/pdfs%2FInvoice%20Q1-2025.pdf?alt=media&token=unique2",
    requestItemData: {
      projectName: "Standard Template",
    },
  },
  {
    id: "3",
    name: "Final Payment Invoice.pdf",
    date: "20 Mar 2025, 1:15 PM",
    uploader: "Alice Johnson",
    url: "https://firebasestorage.googleapis.com/v0/b/start-module-staging-2.appspot.com/o/pdfs%2FFinal%20Payment%20Invoice.pdf?alt=media&token=unique3",
    requestItemData: {
      projectName: "Custom Development",
    },
  },
  {
    id: "4",
    name: "Project Estimate.pdf",
    date: "18 Mar 2025, 11:30 AM",
    uploader: "Bob Williams",
    url: "https://firebasestorage.googleapis.com/v0/b/start-module-staging-2.appspot.com/o/pdfs%2FProject%20Estimate.pdf?alt=media&token=unique4",
    requestItemData: {
      projectName: "Enterprise Project",
    },
  },
];

const styles = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes scaleIn {
    from { transform: scale(0.95); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  .spinner {
    animation: spin 1s linear infinite;
  }

  .upload-modal {
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    animation: fadeIn 0.3s ease;
  }

  .modal-content {
    display: flex;
    width: 100%;
    height: 100%;
    background: #f8fafc;
    animation: scaleIn 0.3s ease;
  }

  .left-sidebar {
    width: 420px;
    min-width: 420px;
    background: white;
    border-right: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .right-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    background: white;
    height: 100%;
  }

  .right-content-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
  }

  .right-content-scroll::-webkit-scrollbar {
    width: 8px;
  }

  .right-content-scroll::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  .right-content-scroll::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;
  }

  .right-content-scroll::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }

  .project-selector {
    padding: 1rem;
    border-bottom: 1px solid #e2e8f0;
    background: white;
    z-index: 2;
  }

  .project-dropdown {
    width: 100%;
    padding: 0.625rem 0.875rem;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    color: #1a202c;
    cursor: pointer;
    transition: all 0.2s;
  }

  .project-dropdown:hover {
    border-color: #cbd5e0;
  }

  .project-dropdown:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  .summary-section {
    padding: 1rem 1rem 0.75rem;
    border-bottom: 1px solid #e2e8f0;
    background: white;
    z-index: 1;
  }

  .summary-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 0.75rem;
    padding-left: 0.25rem;
  }

  .summary-grid {
    display: flex;
    gap: 0.5rem;
    padding-bottom: 0.5rem;
    width: 100%;
  }

  .summary-card {
    flex: 1;
    // width: calc(20% - 0.4rem);
    // min-width: 78px;
    height: 76px;
    min-height: 76px;
    padding: 0.75rem 0.5rem;
    background: #f7fafc;
    border-radius: 0.5rem;
    border: 1px solid #e2e8f0;
    transition: all 0.2s ease;
    cursor: pointer;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .summary-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .summary-card.active {
    border-color: #3B82F6;
    background: #EBF8FF;
    transform: none;
  }

  .summary-label {
    font-size: 0.75rem;
    color: #718096;
    margin-bottom: 0.375rem;
    font-weight: 500;
    line-height: 1.2;
    width: 100%;
    overflow-wrap: break-word;
    hyphens: auto;
  }

  .summary-value {
    font-size: 1.375rem;
    font-weight: 600;
    color: #2d3748;
    line-height: 1.2;
  }

  .summary-card[data-type="duplicates"] .summary-label,
  .summary-card[data-type="amended"] .summary-label {
    font-size: 0.7rem;
    letter-spacing: -0.01em;
  }

  .summary-value.total { color: #2d3748; }
  .summary-value.new { color: #38a169; }
  .summary-value.duplicate { color: #e53e3e; }
  .summary-value.amended { color: #d69e2e; }
  .summary-value.failed { color: #e53e3e; }

  .summary-card.active .summary-value.total { color: #2d3748; }
  .summary-card.active .summary-value.new { color: #38a169; }
  .summary-card.active .summary-value.duplicate { color: #e53e3e; }
  .summary-card.active .summary-value.amended { color: #d69e2e; }
  .summary-card.active .summary-value.failed { color: #e53e3e; }

  .summary-card.active:hover {
    transform: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .invoice-list {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    background: #f8fafc;
    position: relative;
    height: calc(100% - 140px);
  }

  .invoice-list::-webkit-scrollbar {
    width: 8px;
  }

  .invoice-list::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  .invoice-list::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;
  }

  .invoice-list::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }

  .invoice-item {
    padding: 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    margin-bottom: 0.75rem;
    cursor: pointer;
    transition: all 0.2s;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .invoice-item:hover {
    border-color: #4299e1;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  }

  .invoice-item.selected {
    border-color: #4299e1;
    background: #ebf8ff;
    box-shadow: 0 4px 6px rgba(66, 153, 225, 0.1);
  }

  .invoice-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: #2d3748;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .invoice-meta {
    font-size: 0.75rem;
    color: #718096;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .upload-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    background: white;
    border-radius: 1rem;
    border: 2px dashed #e2e8f0;
    margin: 2rem;
    transition: all 0.2s;
  }

  .upload-area.drag-active {
    border-color: #4299e1;
    background: #ebf8ff;
  }

  .upload-icon {
    font-size: 3rem;
    color: #4299e1;
    margin-bottom: 1rem;
  }

  .upload-text {
    font-size: 1rem;
    color: #4a5568;
    margin-bottom: 0.5rem;
  }

  .upload-subtext {
    font-size: 0.875rem;
    color: #718096;
  }

  .action-buttons {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    border-top: 1px solid #e2e8f0;
  }

  .btn {
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .btn-primary {
    background: #4299e1;
    color: white;
    border: none;
  }

  .btn-primary:hover {
    background: #3182ce;
  }

  .btn-secondary {
    background: white;
    color: #4a5568;
    border: 1px solid #e2e8f0;
  }

  .btn-secondary:hover {
    background: #f7fafc;
  }

  .status-badge {
    padding: 0.25rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    margin-left: 0.5rem;
  }

  .status-new {
    background: #C6F6D5;
    color: #22543D;
    border: 1px solid #9AE6B4;
  }

  .status-duplicate {
    background: #FED7D7;
    color: #822727;
    border: 1px solid #FEB2B2;
  }

  .status-amended {
    background: #FEEBC8;
    color: #744210;
    border: 1px solid #FBD38D;
  }

  .status-failed {
    background: #FED7D7;
    color: #822727;
    border: 1px solid #FEB2B2;
  }
`;

const LeftSidebar = ({
  allProjects,
  selectedProject,
  onSelectProject,
  onSelectInvoice,
  selectedInvoiceId,
  staticInvoices,
  filteredInvoices,
  timeElapsed,
  sessionInvoices,
  duplicateIds,
  duplicateCount,
  ammendedIds,
  failedIds
}) => {
  const [selectedFilter, setSelectedFilter] = useState(null);

  const filterInvoices = (filter) => {
    if (selectedFilter === filter) {
      setSelectedFilter(null);
      return;
    }
    setSelectedFilter(filter);
  };

  const getFilteredInvoices = () => {
    if (!selectedFilter) return filteredInvoices;

    switch (selectedFilter) {
      case 'new':
        return filteredInvoices.filter(inv => sessionInvoices.some(si => si.id === inv.id));
      case 'duplicate':
        return filteredInvoices.filter(inv => duplicateIds.has(inv.id));
      case 'amended':
        return filteredInvoices.filter(inv => ammendedIds.includes(inv.id));
      case 'failed':
        return filteredInvoices.filter(inv => failedIds?.includes(inv.id));
      default:
        return filteredInvoices;
    }
  };

  const displayedInvoices = getFilteredInvoices();
  const failedCount = failedIds?.length || 0;

  return (
    <div className="left-sidebar">
      <div className="project-selector">
        <select 
          className="project-dropdown"
          value={selectedProject}
          onChange={(e) => onSelectProject(e.target.value)}
        >
          <option value="All Projects">All Projects</option>
          {allProjects.map((project) => (
            <option key={project} value={project}>
              {project}
            </option>
          ))}
        </select>
      </div>

      <div className="summary-section">
        <h3 className="summary-title">Upload Summary</h3>
        <div className="summary-grid">
          <div 
            className={`summary-card ${selectedFilter === 'total' ? 'active' : ''}`}
            onClick={() => filterInvoices('total')}
            data-type="total"
          >
            <div className="summary-label">Total</div>
            <div className="summary-value total">{filteredInvoices.length}</div>
          </div>
          <div 
            className={`summary-card ${selectedFilter === 'new' ? 'active' : ''}`}
            onClick={() => filterInvoices('new')}
            data-type="new"
          >
            <div className="summary-label">New</div>
            <div className="summary-value new">{sessionInvoices.length}</div>
          </div>
          <div 
            className={`summary-card ${selectedFilter === 'duplicate' ? 'active' : ''}`}
            onClick={() => filterInvoices('duplicate')}
            data-type="duplicates"
          >
            <div className="summary-label">Duplicates</div>
            <div className="summary-value duplicate">{duplicateCount}</div>
          </div>
          <div 
            className={`summary-card ${selectedFilter === 'amended' ? 'active' : ''}`}
            onClick={() => filterInvoices('amended')}
            data-type="amended"
          >
            <div className="summary-label">Amended</div>
            <div className="summary-value amended">{ammendedIds.length}</div>
          </div>
          <div 
            className={`summary-card ${selectedFilter === 'failed' ? 'active' : ''}`}
            onClick={() => filterInvoices('failed')}
            data-type="failed"
          >
            <div className="summary-label">Failed</div>
            <div className="summary-value failed">{failedCount}</div>
          </div>
        </div>
      </div>

      <div className="invoice-list">
        {displayedInvoices.map((invoice) => {
          const isDuplicate = duplicateIds.has(invoice.id);
          const isAmended = ammendedIds.includes(invoice.id);
          const isFailed = failedIds?.includes(invoice.id);
          const isNew = sessionInvoices.some((si) => si.id === invoice.id);
          console.log('displayedInvoices invoice : ', invoice)
          return (
            <div
              key={invoice.id}
              className={`invoice-item ${selectedInvoiceId === invoice.id ? 'selected' : ''}`}
              onClick={() => onSelectInvoice(invoice)}
            >
              <div className="invoice-name">
                {invoice.name}
                {isNew && <span className="status-badge status-new">New</span>}
                {isDuplicate && <span className="status-badge status-duplicate">Duplicate</span>}
                {isAmended && <span className="status-badge status-amended">Amended</span>}
                {isFailed && <span className="status-badge status-failed">Failed</span>}
              </div>
              <div className="invoice-meta">
                <span>{invoice.date}</span>
                <span>•</span>
                <span>{invoice.uploader}</span>
                <span>({invoice.requestItemData.projectName.split('-')[0]})</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// PDF Viewer component using object tag instead of iframe
const PDFViewer = ({ url, title }) => {
  return (
    <object
      data={url}
      type="application/pdf"
      style={{
        width: "100%",
        height: "100%",
        border: "none",
        borderRadius: "8px",
      }}
    >
      <div
        style={{
          padding: "20px",
          textAlign: "center",
        }}
      >
        <p>
          Your browser doesn't support PDF viewing.{" "}
          <a href={url} target="_blank" rel="noopener noreferrer">
            Click here to download the PDF
          </a>
          .
        </p>
      </div>
    </object>
  );
};

// PDF Viewer as embedded image fallback - useful for browsers that don't support object tag properly
const PDFThumbnailViewer = ({ pdfUrl, name }) => {
  
  const [thumbnails, setThumbnails] = useState([]);
  const [loading, setLoading] = useState(true);

  // This is a placeholder - in a real implementation, you would render PDF to images
  // using a library like pdf.js or a server-side rendering service
  useEffect(() => {
    // Simulate loading PDF thumbnails
    setLoading(true);

    // In a real implementation, you would use pdf.js to render the PDF pages as images
    setTimeout(() => {
      // This is just a mockup - you'd actually render the PDF pages
      setThumbnails(["/api/placeholder/800/1000"]);
      setLoading(false);
    }, 1000);
  }, [pdfUrl]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <LoaderIcon />
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        overflow: "auto",
        padding: "20px",
        backgroundColor: "#f8f9fa",
      }}
    >
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          backgroundColor: "white",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
        }}
      >
        {thumbnails.map((src, index) => (
          <div key={index} style={{ padding: "20px" }}>
            <img
              src={src}
              alt={`${name} page ${index + 1}`}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// Right content component
const RightContent = ({
  selectedInvoice,
  setSelectedInvoice,
  isLoading,
  onFileUpload,
  uploadingNewInvoices,
  totalInvoices,
  processedInvoices,
  timeElapsed,
}) => {
  return (
    <div className="right-content">
      {selectedInvoice && !uploadingNewInvoices && (
        <InvoiceHeader
          selectedInvoice={selectedInvoice}
          setSelectedInvoice={setSelectedInvoice}
        />
      )}

      <div className="right-content-scroll">
        {selectedInvoice && !uploadingNewInvoices ? (
          <InvoicePDFViewer
            selectedInvoice={selectedInvoice}
            requestItemData={selectedInvoice.requestItemData}
          />
        ) : null}

        {!selectedInvoice && !uploadingNewInvoices && (
          <UploadInvoicesBox onFileUpload={onFileUpload} />
        )}

        {uploadingNewInvoices && (
          <UploadingInvoices
            totalInvoices={totalInvoices.length}
            totalInvoiceItems={totalInvoices}
            processedInvoices={processedInvoices}
            timeElapsed={timeElapsed}
          />
        )}
      </div>
    </div>
  );
};

const UploadPurchaseOrders = ({ isOpen, onClose, projects = [], userName }) => {

  const [availableProjects, setAvailableProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("All Projects");
  const [sessionInvoices, setSessionInvoices] = useState([]);
  const [staticInvoices, setStaticInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totalInvoices, setTotalInvoices] = useState([]);
  const [processedInvoices, setProcessedInvoices] = useState(0);
  const [checkedInvoices, setCheckedInvoices] = useState([])
  const [uploadingNewInvoices, setUploadingNewInvoices] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [materialMasterfile, setMaterialMasterfile] = useState([]);
  const [duplicateIds, setDuplicateIds] = useState(new Set());
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [ammendedIds, setAmmendedIds] = useState([]);
  const [failedIds, setFailedIds] = useState([]);

  const [writingRequests, setWritingRequests] = useState({
    current: 0,
    total: 0,
    status: "",
  });

  // Update available projects when projects prop or staticInvoices change
  useEffect(() => {
    // Extract project names from project objects
    const processProjects = () => {
      try {
        return projects
          .map((project) => {
            if (typeof project === "string") return project;
            if (project && typeof project === "object") {
              // Check for projectName property
              if (project.projectName) return project.projectName;

              // If no projectName, look for other likely candidate properties
              const possibleNameProps = ["name", "title", "id", "label"];
              for (const prop of possibleNameProps) {
                if (project[prop] && typeof project[prop] === "string") {
                  return project[prop];
                }
              }

              // As a last resort, return a stringified version
              return (
                "Project " +
                (typeof project.id !== "undefined" ? project.id : "Unknown")
              );
            }
            return "Unknown Project";
          })
          .filter(Boolean);
      } catch (error) {
        console.error("Error processing projects:", error);
        return [];
      }
    };

    // Get project names from both sources
    const projectNamesFromProps = processProjects();
    const projectsFromInvoices = staticInvoices
      .map((invoice) => invoice.requestItemData?.projectName)
      .filter(Boolean);

    const allProjects = [
      ...new Set([...projectNamesFromProps, ...projectsFromInvoices]),
    ];
    setAvailableProjects(allProjects);
  }, [projects, staticInvoices]);

  // Filter invoices based on selected project
  useEffect(() => {
    if (selectedProject === "All Projects") {
      setFilteredInvoices(staticInvoices);
    } else {
      const filtered = staticInvoices.filter(
        (invoice) => invoice.requestItemData?.projectName === selectedProject
      );
      setFilteredInvoices(filtered);
    }
  }, [selectedProject, staticInvoices]);

  const areObjectsEqual = (obj1, obj2) => {
    if (
      obj1.requestItemData.poNumber === obj2.poNumber &&
      obj1.requestItemData.poNumber !== "notFound" &&
      obj2.requestItemData.poNumber !== "notFound"
    ) {
      return true;
    }
    return false;
  };

  // Find duplicates and mark invoices accordingly
  const findDuplicates = async (invoices) => {
    const duplicateIds = new Set();
    const ammendedIds = [];
    const invoiceMap = new Map(); // Map invoice id to invoice object with duplicate/data info
  
    // Initialize invoiceMap with copies of invoices (to avoid mutating props)
    invoices.forEach((inv) => {
      invoiceMap.set(inv.id, { ...inv, duplicate: false, data: null });
  
      // Check for amended versions
      const version = inv.requestItemData?.versionNumber;
      if (version && version > 1) {
        ammendedIds.push(inv.id);
      }
    });
  
    // First, check existence for each invoice and add data if exists
    for (let i = 0; i < invoices.length; i++) {
      const currentInvoice = await checkIfExists(invoices[i], projects); // {exists: boolean, data: any}
  
      if (currentInvoice.exists) {
        // Update invoice in map with data and mark duplicate true
        const inv = invoiceMap.get(invoices[i].id);
        invoiceMap.set(invoices[i].id, {
          ...inv,
          duplicate: true,
          data: currentInvoice.data,
        });
        duplicateIds.add(invoices[i].id);
      }
    }
  
    // Now check for duplicates among invoices that don't have exists true
    for (let i = 0; i < invoices.length; i++) {
      const invI = invoiceMap.get(invoices[i].id);
      if (invI.duplicate) continue;
  
      for (let j = i + 1; j < invoices.length; j++) {
        const invJ = invoiceMap.get(invoices[j].id);
        if (invJ.duplicate) continue;
  
        if (areObjectsEqual(invI, invJ)) {
          invoiceMap.set(invI.id, { ...invI, duplicate: true });
          invoiceMap.set(invJ.id, { ...invJ, duplicate: true });
          duplicateIds.add(invI.id);
          duplicateIds.add(invJ.id);
        }
      }
    }
  
    const processed = Array.from(invoiceMap.values());
  
    return {
      duplicateIds,
      duplicateCount: duplicateIds.size,
      ammendedIds,
      processedInvoices: processed,
    };
  };
  
  // Updated useEffect
  useEffect(() => {
    let isMounted = true;
  
    const runFindDuplicates = async () => {
      const { duplicateIds, duplicateCount, ammendedIds, processedInvoices } =
        await findDuplicates(filteredInvoices);
  
      if (isMounted) {
        setDuplicateIds(duplicateIds);
        setDuplicateCount(duplicateCount);
        setAmmendedIds(ammendedIds);
        setCheckedInvoices(processedInvoices);
      }
    };
  
    runFindDuplicates();
  
    return () => {
      isMounted = false;
    };
  }, [filteredInvoices]);

  const updateMasterFile = async () => {
    console.log('projects[0].firmDetailsID : ', projects[0].firmDetailsID)
    let data = await fetchAndReturnMasterfile(String(projects[0].firmDetailsID));
    console.log('updateMasterFile data : ', data)
    setMaterialMasterfile(data);
  }

  useEffect(() => {
    projects.length > 0 && updateMasterFile();
  }, [projects])
  
  if (!isOpen) return null;

  const handleSelectInvoice = (invoice) => {
    setIsLoading(true);
    setSelectedInvoice(invoice);
    // Simulate loading time
    setTimeout(() => {
      setIsLoading(false);
    }, 800);
  };

  const handleSelectProject = (project) => {
    setSelectedProject(project);
  };

  const onFileChange = async (event) => {
    setTimeElapsed(0); // Reset timer
    let startTime = Date.now();

    // Start the timer interval
    const timer = setInterval(() => {
      setTimeElapsed((prevTime) => prevTime + 1);
    }, 1000);

    try {
      await handleFileUpload({
        event,
        handleUpload,
        processInvoice,
        setTotalInvoices,
        setProcessedInvoices,
        setUploadingNewInvoices,
        setStaticInvoices,
        setSelectedInvoice,
        setSessionInvoices,
        userName,
        materialMasterfile,
        firmDetailsID : projects[0].firmDetailsID
      });
    } catch (error) {
      console.error("Error during file upload:", error);
    } finally {
      clearInterval(timer); // Stop the timer
      let endTime = Date.now();
      setTimeElapsed(Math.round((endTime - startTime) / 1000)); // Ensure accurate final time
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.id === "modal-overlay") {
      onClose();
      setSelectedInvoice(null);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div
        id="modal-overlay"
        onClick={handleOverlayClick}
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 50,
          animation: "fadeIn 0.3s ease-out",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            width: "91.666667%",
            height: "83.333333%",
            borderRadius: "12px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            position: "relative",
            display: "flex",
            overflow: "hidden",
            animation: "scaleIn 0.4s ease-out",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="file"
            id="file-upload-input"
            style={{ display: "none" }}
            multiple
            onChange={onFileChange}
          />

          <div
            style={{
              width: "25%",
              height: "100%",
              backgroundColor: "white",
            }}
          >
            <LeftSidebar
              allProjects={availableProjects}
              selectedProject={selectedProject}
              onSelectProject={handleSelectProject}
              onSelectInvoice={handleSelectInvoice}
              selectedInvoiceId={selectedInvoice?.id}
              staticInvoices={staticInvoices}
              filteredInvoices={filteredInvoices}
              timeElapsed={timeElapsed}
              sessionInvoices={sessionInvoices}
              duplicateIds={duplicateIds}
              duplicateCount={duplicateCount}
              ammendedIds={ammendedIds}
              failedIds={failedIds}
            />
          </div>

          <div
            style={{
              width: "75%",
              height: "100%",
              position: "relative",
            }}
          >
            <RightContent
              selectedInvoice={selectedInvoice}
              setSelectedInvoice={setSelectedInvoice}
              isLoading={isLoading}
              onFileUpload={onFileChange}
              uploadingNewInvoices={uploadingNewInvoices}
              totalInvoices={totalInvoices}
              processedInvoices={processedInvoices}
              timeElapsed={timeElapsed}
            />

            {sessionInvoices.length > 0 && uploadingNewInvoices === false && (
              <button
                onClick={async () => {
                  setWritingRequests({
                    current: 0,
                    total: sessionInvoices.length,
                    status: "Starting...",
                  });
                  await createRequest(
                    checkedInvoices,
                    projects,
                    setWritingRequests
                  );

                  // Wait for 1 second before updating the states
                  await new Promise((resolve) => setTimeout(resolve, 1000));

                  setWritingRequests({ current: 0, total: 0, status: "" });
                  setTimeElapsed(0);
                  onClose();
                  window.location.reload();
                }}
                style={{
                  position: "absolute",
                  bottom: "24px",
                  right: "24px",
                  zIndex: 10,
                  backgroundColor: "#3B82F6",
                  color: "white",
                  border: "none",
                  borderRadius: "9999px",
                  padding: "12px 20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease",
                  marginBottom: "50px",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor = "#2563EB")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor = "#3B82F6")
                }
              >
                {writingRequests.current > 0 ? (
                  <>
                    <LoaderIcon />
                    {writingRequests.status}
                  </>
                ) : (
                  <>
                    <SendIcon />
                    Create {sessionInvoices.length} Requests
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export { PDFViewer, PDFThumbnailViewer };
export default UploadPurchaseOrders;
